import { LEADERBOARD, Mediatype, SKIN_SINGLE_AD } from '@pubstack/common/src/adFormat'
import { DisplayedAdUnit, getAdUnitMediatypes, resolveInstreamPlaybackMethod } from '@pubstack/common/src/adunit'
import { cn } from '@pubstack/ui/utils/cn'
import { Fragment, useState } from 'react'
import { Colors } from '~/assets/style/colors'
import { Icon } from '~/components/Icon'
import { Link } from '~/components/Link'
import { Tooltip } from '~/components/Tooltip'
import { WithClassName } from '~/types/utils'
import { INSTREAM_PLCMT_OPTS, INSTREAM_POSITION_OPTS, INSTREAM_START_PLAYING_OPTS } from '../PureAdmAdUnitFirstStep'

const title = 'font-roboto text-base leading-6.5 font-medium'
const attributes = 'flex flex-col gap-3.5 pt-2 pb-3 pl-[34px]'
const label = 'font-roboto text-sm leading-5.5 font-normal text-slate-grey'
const alignedText = 'inline-flex items-center gap-1'
const detailWrapper = 'flex gap-2.5'
const noValueText = 'text-ash italic'
/* `inline-flex` rather than the legacy trigger's `inline`, which ignores the size the info icon is given. */
const infoTooltip = 'inline-flex size-[18px]'

const pipeSeparator = `\xa0 | \xa0`
export type AdUnitMediatypesDetailsProps = WithClassName & {
  adUnit: DisplayedAdUnit
}

export const AdUnitMediatypesDetails = ({ className, adUnit }: AdUnitMediatypesDetailsProps) => {
  const [isCollapsed, setIsCollapsed] = useState(false)
  const adUnitMediatypes: Mediatype[] = getAdUnitMediatypes(adUnit)
  const hasSkin: boolean = adUnit.adFormat?.id === LEADERBOARD.id || adUnit.adFormat?.id === SKIN_SINGLE_AD.id
  return (
    <div className={cn('flex flex-col gap-3', className)}>
      {adUnit.adFormat?.mediatypes.map((mediatype, i) => (
        <Fragment key={i}>
          <div className={alignedText}>
            <Icon width={'26px'} name={adUnitMediatypes.includes(mediatype) ? 'check' : 'close'} fill={adUnitMediatypes.includes(mediatype) ? Colors.Success : Colors.Hurricane} />
            <span>{mediatype}</span>
          </div>
          {mediatype === 'Native' && adUnitMediatypes.includes(mediatype) && adUnit.nativeTemplate === '300x250' && (
            <div className={alignedText}>
              <Icon name={'native_large_rectangle'} height={'36px'} width={'36px'} fill={Colors.Rose} />
              Large rectangle template
            </div>
          )}
          {mediatype === 'Banner' && adUnitMediatypes.includes(mediatype) && (
            <div className={attributes}>
              {hasSkin && (
                <div className={alignedText}>
                  <Icon width={'26px'} name={adUnit.skinEnabled ? 'check' : 'close'} fill={adUnit.skinEnabled ? Colors.Success : Colors.Hurricane} />
                  <span>Skin</span>
                </div>
              )}
              <div className={alignedText}>
                <Icon width={'26px'} name={adUnit.fluid ? 'check' : 'close'} fill={adUnit.fluid ? Colors.Success : Colors.Hurricane} />
                <span>Fluid</span>
              </div>
              <div className={detailWrapper}>
                <div>
                  <div className={alignedText}>
                    <h3 className={label}>Standard sizes</h3>
                    <Tooltip className={infoTooltip} title={'Standard sizes (px) - ad server, header bidding'}>
                      <Icon width={'16px'} name={'info'} fill={Colors.Hurricane} />
                    </Tooltip>
                  </div>
                  <div>{adUnit.sizes && adUnit.sizes.length > 0 ? <span>{adUnit.sizes?.join(pipeSeparator)}</span> : <div className={noValueText}>No standard sizes.</div>}</div>
                </div>
              </div>
              <div className={detailWrapper}>
                <div>
                  <div className={alignedText}>
                    <h3 className={label}>Custom sizes</h3>
                    <Tooltip className={infoTooltip} title={'Custom sizes (px) - ad server only'}>
                      <Icon width={'16px'} name={'info'} fill={Colors.Hurricane} />
                    </Tooltip>
                  </div>
                  <div>{adUnit.customSizes && adUnit.customSizes.length > 0 ? <span>{adUnit.customSizes?.join(pipeSeparator)}</span> : <div className={noValueText}>No custom sizes.</div>}</div>
                </div>
              </div>
            </div>
          )}
          {mediatype === 'Instream' && adUnit.instreamParams && adUnitMediatypes.includes('Instream') && (
            <div className={attributes}>
              <div className={detailWrapper}>
                <div>
                  <div className={alignedText}>
                    <h3 className={label}>Player size</h3>
                  </div>
                  <div>{adUnit.sizes && adUnit.sizes.length > 0 ? <span>{adUnit.sizes?.join(pipeSeparator)}</span> : <div className={noValueText}>No standard sizes.</div>}</div>
                </div>
              </div>
              <div className={'flex flex-row gap-5'}>
                <div className={cn(detailWrapper, 'w-[209px]')}>
                  <div>
                    <div className={alignedText}>
                      <h3 className={label}>Minimal ad duration</h3>
                    </div>
                    <div>
                      <span>{`${adUnit.instreamParams.mediaTypeParams.minduration} s`}</span>
                    </div>
                  </div>
                </div>
                <div className={cn(detailWrapper, 'w-[209px]')}>
                  <div>
                    <div className={alignedText}>
                      <h3 className={label}>Maximal ad duration</h3>
                    </div>
                    <div>
                      <span>{`${adUnit.instreamParams.mediaTypeParams.maxduration} s`}</span>
                    </div>
                  </div>
                </div>
              </div>
              <Link
                label={'Advanced settings'}
                icon={isCollapsed ? 'chevron_up' : 'chevron_down'}
                trackingLabel={'adm-ad-units-instream-advanced-settings-set'}
                onClick={() => setIsCollapsed(!isCollapsed)}
              />
              {isCollapsed && (
                <>
                  <h2 className={title}>Playing settings</h2>
                  <div className={detailWrapper}>
                    <div>
                      <div className={alignedText}>
                        <h3 className={label}>Start playing when</h3>
                      </div>
                      <div>
                        <span>{INSTREAM_START_PLAYING_OPTS.find((a) => a.value === (adUnit.instreamParams ? resolveInstreamPlaybackMethod(adUnit.instreamParams) : undefined))?.label}</span>
                      </div>
                    </div>
                  </div>
                  <div className={detailWrapper}>
                    <div>
                      <div className={alignedText}>
                        <h3 className={label}>Position on screen (pos)</h3>
                      </div>
                      <div>
                        <span>{INSTREAM_POSITION_OPTS.find((a) => a.value === adUnit.instreamParams?.mediaTypeParams.pos)?.label}</span>
                      </div>
                    </div>
                  </div>
                  <div className={detailWrapper}>
                    <div>
                      <div className={alignedText}>
                        <h3 className={label}>Allow skippable ads</h3>
                      </div>
                      <div>
                        <span>{adUnit.instreamParams.skippableads ? 'Yes' : 'No'}</span>
                      </div>
                    </div>
                  </div>
                  <div className={detailWrapper}>
                    <div>
                      <div className={alignedText}>
                        <h3 className={label}>Placement (plcmt)</h3>
                      </div>
                      <div>
                        <span>{INSTREAM_PLCMT_OPTS.find((a) => a.value === adUnit.instreamParams?.mediaTypeParams.plcmt)?.label}</span>
                      </div>
                    </div>
                  </div>
                  <h2 className={title}>Video settings</h2>
                  <div className={'flex flex-row gap-5'}>
                    <div className={cn(detailWrapper, 'w-[209px]')}>
                      <div>
                        <div className={alignedText}>
                          <h3 className={label}>Minimal bit rate</h3>
                        </div>
                        <div>
                          <span>{`${adUnit.instreamParams.mediaTypeParams.minbitrate} kbps`}</span>
                        </div>
                      </div>
                    </div>
                    <div className={cn(detailWrapper, 'w-[209px]')}>
                      <div>
                        <div className={alignedText}>
                          <h3 className={label}>Maximal bit rate</h3>
                        </div>
                        <div>
                          <span>{`${adUnit.instreamParams.mediaTypeParams.maxbitrate} kbps`}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className={detailWrapper}>
                    <div>
                      <div className={alignedText}>
                        <h3 className={label}>Video protocols allowed</h3>
                      </div>
                      <div>
                        <span>{adUnit.instreamParams.protocols?.join(pipeSeparator)}</span>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </Fragment>
      ))}
    </div>
  )
}
